import * as THREE from 'three';
import { lineEndpoints } from './line3d';
import type { DrawingEntity, Vec3 } from '../types';

const same = (a: Vec3, b: Vec3) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) < .01;

export function faceBasis(vertices: Vec3[]) {
  if (vertices.length < 3) return null;
  const origin = new THREE.Vector3(...vertices[0]);
  const u = new THREE.Vector3(...vertices[1]).sub(origin);
  if (u.length() < .001) return null;
  u.normalize();
  const normal = new THREE.Vector3();
  for (let i = 0; i < vertices.length; i++) {
    const a = vertices[i], b = vertices[(i + 1) % vertices.length];
    normal.x += (a[1] - b[1]) * (a[2] + b[2]);
    normal.y += (a[2] - b[2]) * (a[0] + b[0]);
    normal.z += (a[0] - b[0]) * (a[1] + b[1]);
  }
  if (normal.length() < .001) return null;
  normal.normalize();
  if (normal.y < -.5) normal.negate();
  const v = new THREE.Vector3().crossVectors(normal, u).normalize();
  const points = vertices.map((p) => {
    const offset = new THREE.Vector3(...p).sub(origin);
    return { x: offset.dot(u), y: offset.dot(v), plane: offset.dot(normal) };
  });
  if (points.some((point) => Math.abs(point.plane) > .01)) return null;
  const area = points.reduce((sum, p, i) => { const q = points[(i + 1) % points.length]; return sum + p.x * q.y - q.x * p.y; }, 0) / 2;
  if (Math.abs(area) < .001) return null;
  return { origin, u, v, normal, points, area };
}

/** When a new edge closes a simple planar chain, replace that chain with one face. */
export function closedFaceFromLines(existing: DrawingEntity[], start: Vec3, end: Vec3): { vertices: Vec3[]; consumedIds: string[] } | null {
  const edges = existing.filter((drawing) => drawing.kind === 'line').map((drawing) => ({ id: drawing.id, ends: lineEndpoints(drawing) }));
  const queue: Array<{ at: Vec3; vertices: Vec3[]; ids: string[] }> = [{ at: end, vertices: [start, end], ids: [] }];
  while (queue.length) {
    const path = queue.shift()!;
    for (const edge of edges) {
      if (path.ids.includes(edge.id)) continue;
      const next = same(edge.ends[0], path.at) ? edge.ends[1] : same(edge.ends[1], path.at) ? edge.ends[0] : null;
      if (!next) continue;
      if (same(next, start)) {
        if (path.ids.length < 1) continue;
        const basis = faceBasis(path.vertices);
        if (basis && !hasCrossing(basis.points)) return { vertices: path.vertices, consumedIds: [...path.ids, edge.id] };
      } else if (path.vertices.length < edges.length + 2 && !path.vertices.some((point) => same(point, next))) {
        queue.push({ at: next, vertices: [...path.vertices, next], ids: [...path.ids, edge.id] });
      }
    }
  }
  return null;
}

function hasCrossing(points: Array<{ x: number; y: number }>) {
  const cross = (a: typeof points[number], b: typeof points[number], c: typeof points[number]) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  for (let i = 0; i < points.length; i++) for (let j = i + 2; j < points.length; j++) {
    if (i === 0 && j === points.length - 1) continue;
    const a = points[i], b = points[(i + 1) % points.length], c = points[j], d = points[(j + 1) % points.length];
    if (cross(a, b, c) * cross(a, b, d) < 0 && cross(c, d, a) * cross(c, d, b) < 0) return true;
  }
  return false;
}

export function createFaceGeometry(vertices: Vec3[], extrusionMm = 0): THREE.BufferGeometry | null {
  const basis = faceBasis(vertices);
  if (!basis) return null;
  const shape = new THREE.Shape();
  basis.points.forEach((point, i) => i === 0 ? shape.moveTo(point.x / 1000, point.y / 1000) : shape.lineTo(point.x / 1000, point.y / 1000));
  shape.closePath();
  const geometry = Math.abs(extrusionMm) < .001
    ? new THREE.ShapeGeometry(shape)
    : new THREE.ExtrudeGeometry(shape, { depth: extrusionMm / 1000, bevelEnabled: false });
  const transform = new THREE.Matrix4().makeBasis(basis.u, basis.v, basis.normal);
  transform.setPosition(basis.origin.multiplyScalar(.001));
  geometry.applyMatrix4(transform);
  return geometry;
}
